<template>
  <Dialog
    id="popup-activity-form"
    v-model:visible="displayShow"
    :modal="true"
    :close-on-escape="false"
    class="popup"
  >
    <template #header>
      <strong>
        <font-awesome-icon icon="clipboard-list" />
        Activity
        <router-link
          v-if="rotaActivity"
          :to="{
            name: 'activity/RotaActivityView',
            params: {
              rotaActivityId: rotaActivity.id,
            },
          }"
          target="_blank"
        >
          <font-awesome-icon icon="external-link-alt" />
        </router-link>
      </strong>
    </template>
    <Rota-activity-view
      :rota-activity-id="rotaActivity.id"
      :embedded="true"
      @rota-activity-updated="onRotaActivityUpdated"
      @rota-activity-deleted="onRotaActivityDeleted"
    />
  </Dialog>
</template>

<script>
import {
  ref,
  onBeforeMount,
  onBeforeUnmount,
  watch,
  defineAsyncComponent,
  toRaw,
} from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import get from "lodash/get";
import has from "lodash/has";
import Dialog from "primevue/dialog";
import { Hub } from "aws-amplify/utils";
import { useAbility } from "@casl/vue";
// import useAutoStartActivity from "@/components/activity/report/functions/useAutoStartActivity";
import useFormSequenceModal from "@/components/form/useFormSequenceModal";
import useCreateActivitySequence from "@/components/form/sequences/useCreateActivitySequence";

export default {
  name: "PopupActivityForm",
  components: {
    // eslint-disable-next-line vue/no-reserved-component-names
    Dialog,
    RotaActivityView: defineAsyncComponent(() => {
      return import("@/views/activity/RotaActivityView.vue");
    }),
  },
  setup: () => {
    const displayShow = ref(false);
    const allowAutoStartActivity = ref(true);
    const allowAttachToRota = ref(true);
    const rotaActivity = ref({});
    const store = useStore();
    const formState = ref("");
    const route = useRoute();
    const { can } = useAbility();
    // const { autoStartActivity } = useAutoStartActivity();

    let popupActivityListenerCancel;

    const {
      modalVisible: formSequenceModalVisible,
      showFormSequenceModal,
      fs,
    } = useFormSequenceModal();

    onBeforeUnmount(() => {
      popupActivityListenerCancel();
    });
    onBeforeMount(() => {
      popupActivityListenerCancel = Hub.listen(
        "popup-activity",
        ({ payload }) => {
          const passthruPayload = { ...payload?.data };
          switch (payload.event) {
            case "add":
            case "edit":
              if (has(payload.data, "rotaActivity")) {
                rotaActivity.value = payload.data.rotaActivity;
                delete passthruPayload.rotaActivity;
              }
              // update display options
              allowAutoStartActivity.value = get(
                payload.data,
                "allowAutoStartActivity",
                true,
              );
              delete passthruPayload.allowAutoStartActivity;
              allowAttachToRota.value = get(
                payload.data,
                "allowAttachToRota",
                true,
              );
              delete passthruPayload.allowAttachToRota;
              break;
          }

          switch (payload.event) {
            case "add":
              if (!has(payload.data, "rotaActivity")) {
                rotaActivity.value = {};
              }
              showFormSequenceModal(
                "Add Activity",
                "clipboard-list",
                useCreateActivitySequence,
                {
                  activity: toRaw(rotaActivity.value),
                  allowAutoStartActivity: allowAutoStartActivity.value,
                  allowAttachToRota: allowAttachToRota.value,
                  ...passthruPayload,
                  userIsAdmin: can("manage", "rota.rota-activities"),
                },
                saveNewRotaActivity,
              );
              fs.value.resetState();

              break;
            case "edit":
              showFormSequenceModal(
                "Edit Activity",
                "clipboard-list",
                useCreateActivitySequence,
                {
                  activity: toRaw(rotaActivity.value),
                  allowAutoStartActivity: allowAutoStartActivity.value,
                  allowAttachToRota: allowAttachToRota.value,
                  ...passthruPayload,
                  userIsAdmin: can("manage", "rota.rota-activities"),
                },
                updateRotaActivity,
              );

              break;
            case "view":
              if (has(payload.data, "rotaActivity")) {
                rotaActivity.value = payload.data.rotaActivity;
              }
              displayShow.value = true;
              break;
            case "hide":
              displayShow.value = false;
              break;
            default:
          }
        },
      );
    });

    // hook into router. if the route changes, hide the modal windows.
    watch(
      () => route.fullPath,
      async () => {
        displayShow.value = false;
        formSequenceModalVisible.value = false;
      },
    );

    const saveNewRotaActivity = async (data) => {
      const postData = {
        ...data.body,
      };
      if (data.startForClients) {
        postData.startForClients = data.startForClients;
      }

      const updatedData = await store.dispatch(
        "rotaactivities/createRotaActivity",
        postData,
      );

      if (updatedData === false) {
        return false;
      }

      Hub.dispatch("activity", {
        event: "created",
        data: updatedData,
      });
      Hub.dispatch("toast", {
        event: "success",
        data: {
          timeout: 5000,
        },
        message: {
          component: defineAsyncComponent(
            () => import("@/components/ui/ToastWithLink.vue"),
          ),
          props: {
            message: "Activity added.",
            link: {
              text: "Click to view",
              name: "activity/RotaActivityView",
              params: {
                rotaActivityId: updatedData.id,
              },
            },
          },
        },
      });
      formSequenceModalVisible.value = false;
    };

    const updateRotaActivity = async (data) => {
      const updatedData = await store.dispatch(
        "rotaactivities/updateRotaActivity",
        {
          id: data.id,
          changes: data.body,
        },
      );
      if (updatedData === false) {
        return false;
      }
      Hub.dispatch("activity", {
        event: "updated",
        data: updatedData,
      });
      Hub.dispatch("toast", {
        event: "success",
        message: "Activity updated",
        data: {
          timeout: 3000,
        },
        // @todo in the future - show a component with a link to the new activity
        link: {
          name: "activity/RotaActivityView",
          params: {
            rotaActivityId: updatedData.id,
          },
        },
      });
      formSequenceModalVisible.value = false;
    };

    const onRotaActivityUpdated = (updatedData) => {
      Hub.dispatch("activity", {
        event: "updated",
        data: updatedData,
      });
    };

    const onRotaActivityDeleted = (updatedData) => {
      Hub.dispatch("activity", {
        event: "deleted",
        data: updatedData,
      });
    };

    return {
      displayShow,
      rotaActivity,
      formState,
      saveNewRotaActivity,
      onRotaActivityUpdated,
      onRotaActivityDeleted,
    };
  },
};
</script>

<style lang="scss">
#popup-activity-form .rota-activity-form {
  min-height: 30em;
}
</style>
